import { useCallback, useEffect, useMemo, useState } from 'react'
import { track } from '@amplitude/analytics-browser'
import { useRouter } from 'next/router'

import {
  BUTTON_CLICK,
  CLICK_RECOMMENDED_KEYWORD,
  CLICK_RECOMMENDED_QUESTION,
  CLICK_TICKER_OPTION,
  PAGE_VIEW,
  SHOW,
  VIEW_PRODUCT_UPDATES,
  CLICK_PRODUCT_UPDATES_LOAD_MORE,
  // Recents / Chat Search Events
  OPEN_CHAT_SEARCH,
  CLICK_CHAT_SEARCH_CONVERSATION,
  CLICK_CHAT_SEARCH_NEW_CHAT,
  VIEW_RECENTS,
  CLICK_RECENTS_CONVERSATION,
  TOGGLE_RECENTS_FAVORITE,
  RENAME_RECENTS_CONVERSATION,
  DELETE_RECENTS_CONVERSATION,
  OPEN_RECENTS_ORGANIZE_MODAL,
  APPLY_RECENTS_ORGANIZE,
  VIEW_ARTIFACTS,
  CLICK_ARTIFACTS_TAB,
  OPEN_ARTIFACT,
  PIN_ARTIFACT,
  UNPIN_ARTIFACT,
  SEARCH_ARTIFACTS,
  RENAME_ARTIFACT,
  CLICK_ARTIFACTS_SHARE,
  OPEN_ARTIFACT_CONVERSATION,
  TOGGLE_ARTIFACTS_PINNED_FILTER,
  LOAD_ARTIFACTS_PAGE,
  VIEW_ARTIFACTS_SEARCH_RESULTS,
  CLICK_FAVORITE_CONVERSATION,
  //Company Page Events
  VIEW_COMPANY_PAGE,
  OPEN_COMPANY_PAGE_PANEL,
  CLICK_COMPANY_PAGE_TAB,
  TOGGLE_COMPANY_PAGE_PANEL,
  CLICK_COMPANY_PAGE_SUBTAB,
  CLICK_COMPANY_PAGE_WIDGET,
  CLICK_COMPANY_PAGE_SHARE,
  CLICK_COMPANY_PAGE_EXPORT,
  //Reddit Report Events
  VIEW_REDDIT_REPORT,
  OPEN_REDDIT_REPORT_PANEL,
  TOGGLE_REDDIT_REPORT_PANEL,
  CLICK_REDDIT_REPORT_WIDGET,
  //Feedback Events
  CLICK_FEEDBACK,
  //Briefing Agent Events
  VIEW_BRIEFING_AGENT_PAGE,
  OPEN_BRIEFING_AGENT_MODAL,
  CLOSE_BRIEFING_AGENT_MODAL,
  CLICK_BRIEFING_AGENT_CARD,
  SELECT_BRIEFING_AGENT_SOURCE,
  CHANGE_BRIEFING_AGENT_FREQUENCY,
  ADD_BRIEFING_AGENT_TICKER,
  REMOVE_BRIEFING_AGENT_TICKER,
  COMPLETE_BRIEFING_AGENT_SETUP,
  TOGGLE_BRIEFING_AGENT_STATUS,
  DELETE_BRIEFING_AGENT,
  SEARCH_BRIEFING_AGENT_HISTORY,
  FILTER_BRIEFING_AGENT_HISTORY,
  CLICK_BRIEFING_AGENT_HISTORY_PAGE,
  TOGGLE_BRIEFING_AGENT_HISTORY_PANEL,
  CLICK_BRIEFING_AGENT_HISTORY_CARD,
  CLICK_BRIEFING_AGENT_HISTORY_BUTTON,
  SELECT_BRIEFING_AGENT_SKILL,
  CLICK_BRIEFING_AGENT_PREVIEW,
  //Primer Events
  CLICK_PRIMER_EXPORT,
  //Analytics Trending Events
  CLICK_ANALYTICS_TRENDING_QUESTION,
  //Analytics Skill Events
  CLICK_ANALYTICS_SKILL,
  // Storage Vault Folder Events
  CLICK_STORAGE_CREATE_FOLDER,
  CLICK_STORAGE_MOVE_FILE,
  // RMS Viewer Events
  CLICK_RMS_VIEWER_DOWNLOAD,

  // Investment Profile Gate Events
  VIEW_INVESTMENT_PROFILE_GATE,
  COMPLETE_INVESTMENT_PROFILE_STEP,
  SUBMIT_INVESTMENT_PROFILE,
  CLOSE_INVESTMENT_PROFILE_GATE,

  // Meeting Agent Events
  VIEW_MEETING_AGENT_BLOCKED_LINK,
} from '@/constants/amplitudeEvents'
import { InvestmentProfileStepKey } from '@/lib/investmentProfile'
import { MeetingLinkBlockReason } from '@/lib/meetingRegistrationLink'
import { BriefFrequency, BriefSourceType } from '@/types/briefing_agent.type'
import { User } from '@/types/user.type'
import { Stock } from '@/types/stock'
import type { ThirdBridgeTranscript } from '@/lib/thirdBridge/types'
import { WorkspaceSkill } from '@/types/workspace_skill.type'
import { SkillGroup } from '@/types/skill_group.type'
import { Side } from '@/types/investment_profile.type'

// Why the gate was left without finishing. Fixed set so the funnel drop-off stays segmentable.
export type GateExitReason = 'page_hide'

// Skill and folder names outside the official scope are written by analysts and can name
// people, clients or positions, so only official names leave; the ids always do.
export const trackableSkillName = (skill: {
  skill_name: string
  skill_scope: WorkspaceSkill['scope']
}) => (skill.skill_scope === 'official' ? skill.skill_name : undefined)

// Folders whose names are provisioned by the organization or by Linq rather than typed by a user.
const TRACKABLE_FOLDER_SOURCES = new Set<SkillGroup['source']>(['organization', 'favorite'])

export const trackableFolderName = (folder: {
  folder_name?: string
  folder_source?: SkillGroup['source']
}) =>
  folder.folder_source && TRACKABLE_FOLDER_SOURCES.has(folder.folder_source)
    ? folder.folder_name
    : undefined

const useAmplitudeTracking = (user?: User | null) => {
  const router = useRouter()
  const [pageViewProperties, setPageViewProperties] = useState({})

  const defaultEventProps = useMemo(() => {
    const organization = user?.organization

    return {
      path: router.pathname,
      group: user?.group,
      organization_name: organization?.name,
      organization_id: organization?.id,
    }
  }, [router.pathname, user])

  useEffect(() => {
    setPageViewProperties({
      referrer: window.document.referrer,
      referring_domain: window.document.referrer.split('/')[2],
      page_domain: window.location.hostname,
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
    })
  }, [])

  const trackPageView = useCallback(
    (eventProperties: { [key: string]: any } = {}) => {
      track(PAGE_VIEW, { ...defaultEventProps, ...pageViewProperties, ...eventProperties })
    },
    [defaultEventProps, pageViewProperties]
  )

  const trackButtonClick = useCallback(
    (name: string, eventProperties: { [key: string]: any } = {}) => {
      track(BUTTON_CLICK, { name, ...defaultEventProps, ...eventProperties })
    },
    [defaultEventProps]
  )

  // Event Map events are named "[Action] Event Map [Object]" (see
  // constants/amplitudeEvents) and always carry the default org props so the
  // pilot dashboards can filter by organization_id.
  const trackEventMap = useCallback(
    (name: string, eventProperties: { [key: string]: any } = {}) => {
      track(name, { ...defaultEventProps, ...eventProperties })
    },
    [defaultEventProps]
  )

  const trackShow = useCallback(
    (name: string, eventProperties: { [key: string]: any } = {}) => {
      track(SHOW, { name, ...defaultEventProps, ...eventProperties })
    },
    [defaultEventProps]
  )

  // Never pass the link itself: meeting links can carry passcodes.
  // blacklist_pattern is null when block_reason is not the URL blacklist.
  const trackViewMeetingAgentBlockedLink = (eventProperties: {
    block_reason: MeetingLinkBlockReason
    blacklist_pattern: string | null
    link_host: string | null
  }) => {
    track(VIEW_MEETING_AGENT_BLOCKED_LINK, { ...defaultEventProps, ...eventProperties })
  }

  const trackViewProductUpdates = (eventProperties: { language: string; total_count: number }) => {
    track(VIEW_PRODUCT_UPDATES, { ...defaultEventProps, ...eventProperties })
  }

  const trackClickProductUpdatesLoadMore = (eventProperties: { page: number }) => {
    track(CLICK_PRODUCT_UPDATES_LOAD_MORE, { ...defaultEventProps, ...eventProperties })
  }

  const trackClickRecommendedKeyword = (
    keyword: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_RECOMMENDED_KEYWORD, { keyword, ...eventProperties })
  }

  const trackClickRecommendedQuestion = (
    keyword: string,
    question: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_RECOMMENDED_QUESTION, { keyword, question, ...eventProperties })
  }

  const trackClickTickerOption = (stock: Stock, eventProperties: { [key: string]: any } = {}) => {
    track(CLICK_TICKER_OPTION, {
      stock_id: stock.id,
      ticker: stock.ticker,
      name: stock.name,
      ...eventProperties,
    })
  }

  //Company Page Events
  const trackCompanyPageView = (stock: Stock, eventProperties: { [key: string]: any } = {}) => {
    track(VIEW_COMPANY_PAGE, {
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackCompanyPageOpenPanel = (
    stock: Stock,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(OPEN_COMPANY_PAGE_PANEL, {
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickCompanyPageTab = (
    tabName: string,
    stock: Stock,
    panelExpanded: boolean,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_COMPANY_PAGE_TAB, {
      tab_name: tabName,
      panel_expanded: panelExpanded,
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackToggleCompanyPagePanel = (
    panelExpanded: boolean,
    stock: Stock,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(TOGGLE_COMPANY_PAGE_PANEL, {
      panel_expanded: panelExpanded,
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickCompanyPageSubtab = (
    tabName: string,
    subtabName: string,
    stock: Stock,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_COMPANY_PAGE_SUBTAB, {
      tab_name: tabName,
      subtab_name: subtabName,
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickCompanyPageWidget = (
    widgetElement: string,
    stock: Stock,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_COMPANY_PAGE_WIDGET, {
      widget_element: widgetElement,
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickCompanyPageShare = (
    stock: Stock,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_COMPANY_PAGE_SHARE, {
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickCompanyPageExport = (
    exportType: string,
    stock: Stock,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_COMPANY_PAGE_EXPORT, {
      export_type: exportType,
      stock_id: stock.id,
      stock_ticker: stock.ticker,
      stock_name: stock.name,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  //Reddit Report Events
  const trackRedditReportView = (eventProperties: { [key: string]: any } = {}) => {
    track(VIEW_REDDIT_REPORT, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackRedditReportOpenPanel = (eventProperties: { [key: string]: any } = {}) => {
    track(OPEN_REDDIT_REPORT_PANEL, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackToggleRedditReportPanel = (
    isExpanded: boolean,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(TOGGLE_REDDIT_REPORT_PANEL, {
      panel_expanded: isExpanded,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickRedditReportWidget = (
    widgetElement: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_REDDIT_REPORT_WIDGET, {
      widget_element: widgetElement,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  //Feedback Events
  const trackClickFeedback = (
    feedbackType: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_FEEDBACK, {
      feedback_type: feedbackType,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  // Briefing Agent Events
  const trackViewBriefingAgentPage = (eventProperties: { brief_count: number }) => {
    track(VIEW_BRIEFING_AGENT_PAGE, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackOpenBriefingAgentModal = (eventProperties: {
    is_edit_mode: boolean
    brief_id?: string
    is_read_only?: boolean
  }) => {
    track(OPEN_BRIEFING_AGENT_MODAL, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackCloseBriefingAgentModal = (eventProperties: {
    is_edit_mode: boolean
    close_method: 'cancel' | 'close_button' | 'outside_click'
  }) => {
    track(CLOSE_BRIEFING_AGENT_MODAL, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickBriefingAgentCard = (eventProperties: {
    brief_id: string
    source_type: BriefSourceType
    frequency: BriefFrequency
    is_active: boolean
    ticker_count: number
  }) => {
    track(CLICK_BRIEFING_AGENT_CARD, {
      source_count: eventProperties.source_type === 'both' ? 2 : 1,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackSelectBriefingAgentSource = (
    sourceType: BriefSourceType,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(SELECT_BRIEFING_AGENT_SOURCE, {
      source_type: sourceType,
      source_count: sourceType === 'both' ? 2 : 1,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackChangeBriefingAgentFrequency = (eventProperties: {
    previous_frequency: BriefFrequency
    new_frequency: BriefFrequency
    is_edit_mode: boolean
  }) => {
    track(CHANGE_BRIEFING_AGENT_FREQUENCY, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackAddBriefingAgentTicker = (eventProperties: {
    ticker: string
    ticker_count: number
    is_edit_mode: boolean
  }) => {
    track(ADD_BRIEFING_AGENT_TICKER, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackRemoveBriefingAgentTicker = (eventProperties: {
    ticker: string
    ticker_count: number
    is_edit_mode: boolean
  }) => {
    track(REMOVE_BRIEFING_AGENT_TICKER, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackCompleteBriefingAgentSetup = (eventProperties: {
    source_type: BriefSourceType
    frequency: BriefFrequency
    is_edit_mode: boolean
    ticker_count: number
  }) => {
    track(COMPLETE_BRIEFING_AGENT_SETUP, {
      source_count: eventProperties.source_type === 'both' ? 2 : 1,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackToggleBriefingAgentStatus = (eventProperties: {
    brief_id: string
    previous_status: boolean
    new_status: boolean
    source_type: BriefSourceType
    frequency: BriefFrequency
  }) => {
    track(TOGGLE_BRIEFING_AGENT_STATUS, {
      source_count: eventProperties.source_type === 'both' ? 2 : 1,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackDeleteBriefingAgent = (eventProperties: {
    brief_id: string
    source_type: BriefSourceType
    frequency: BriefFrequency
    was_active: boolean
    ticker_count: number
  }) => {
    track(DELETE_BRIEFING_AGENT, {
      source_count: eventProperties.source_type === 'both' ? 2 : 1,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackSearchBriefingAgentHistory = (eventProperties: { query_length: number }) => {
    track(SEARCH_BRIEFING_AGENT_HISTORY, { ...defaultEventProps, ...eventProperties })
  }

  const trackFilterBriefingAgentHistory = (eventProperties: {
    brief_id: string | null
    is_all_briefs: boolean
  }) => {
    track(FILTER_BRIEFING_AGENT_HISTORY, { ...defaultEventProps, ...eventProperties })
  }

  const trackClickBriefingAgentHistoryPage = (eventProperties: { page: number }) => {
    track(CLICK_BRIEFING_AGENT_HISTORY_PAGE, { ...defaultEventProps, ...eventProperties })
  }

  const trackToggleBriefingAgentHistoryPanel = (eventProperties: { panel_expanded: boolean }) => {
    track(TOGGLE_BRIEFING_AGENT_HISTORY_PANEL, { ...defaultEventProps, ...eventProperties })
  }

  const trackClickBriefingAgentHistoryCard = (eventProperties: {
    delivery_id: string
    delivery_status: string
  }) => {
    track(CLICK_BRIEFING_AGENT_HISTORY_CARD, { ...defaultEventProps, ...eventProperties })
  }

  const trackClickBriefingAgentHistoryButton = (eventProperties: { brief_id: string }) => {
    track(CLICK_BRIEFING_AGENT_HISTORY_BUTTON, { ...defaultEventProps, ...eventProperties })
  }

  const trackSelectBriefingAgentSkill = (eventProperties: {
    skill_id: string
    skill_name: string
    skill_scope: WorkspaceSkill['scope']
  }) => {
    track(SELECT_BRIEFING_AGENT_SKILL, {
      ...defaultEventProps,
      ...eventProperties,
      skill_name: trackableSkillName(eventProperties),
    })
  }

  const trackClickBriefingAgentPreview = (eventProperties: {
    is_edit_mode: boolean
    ticker_count: number
    source_type: BriefSourceType
    frequency: BriefFrequency
  }) => {
    track(CLICK_BRIEFING_AGENT_PREVIEW, {
      source_count: eventProperties.source_type === 'both' ? 2 : 1,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  //Analytics Trending Events
  const trackClickAnalyticsTrendingQuestion = (eventProperties: {
    trending_question_id: string
    trending_question_text: string
    trending_position: number
  }) => {
    track(CLICK_ANALYTICS_TRENDING_QUESTION, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  // Analytics Skill Events — main-page skill card click (PRD #3680, COV-96).
  // The folder fields are optional because the card can now be reached without one:
  // the Skills module lists skills directly. They are omitted rather than filled with a
  // placeholder, so the folder breakdown built for #3680 keeps counting real folders only.
  const trackClickAnalyticsSkill = (eventProperties: {
    skill_id: string
    skill_name: string
    skill_scope: WorkspaceSkill['scope']
    folder_id?: string
    folder_name?: string
    folder_source?: SkillGroup['source']
  }) => {
    track(CLICK_ANALYTICS_SKILL, {
      ...defaultEventProps,
      ...eventProperties,
      skill_name: trackableSkillName(eventProperties),
      folder_name: trackableFolderName(eventProperties),
    })
  }

  //Primer Events
  const trackClickPrimerExport = (
    exportType: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_PRIMER_EXPORT, {
      export_type: exportType,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  // Storage Vault Folder Events
  const trackClickStorageCreateFolder = (eventProperties: {
    workspace: string
    parent_container_id: string | null
  }) => {
    track(CLICK_STORAGE_CREATE_FOLDER, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickStorageMoveFile = (eventProperties: {
    workspace: string
    target_container_id: string | null
    file_count: number
  }) => {
    track(CLICK_STORAGE_MOVE_FILE, {
      ...defaultEventProps,
      source: 'vault',
      ...eventProperties,
    })
  }

  // RMS Viewer Events — original-file save from the reference viewer (LIN-650).
  // The viewer is a public route, so this fires for readers with no session too;
  // `is_authenticated` is what separates the two populations in analysis.
  const trackClickRmsViewerDownload = (eventProperties: {
    source: string
    format: string
    // Closed set, unlike source/format, which are meant to grow as the viewer
    // learns to hand over other files. Derived from the transcript union rather
    // than repeated as a literal, so a new shape cannot reach the event without
    // this failing to compile first.
    transcript_kind: ThirdBridgeTranscript['kind']
    is_authenticated: boolean
    // How long the saved transcript is, for every format. We do not know the real
    // distribution of call lengths; this is how it gets measured (LIN-897 FR-6).
    transcript_block_count?: number
  }) => {
    track(CLICK_RMS_VIEWER_DOWNLOAD, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  // Recents / Chat Search Events (Recents Step 1)
  const trackOpenChatSearch = (source: string, eventProperties: { [key: string]: any } = {}) => {
    track(OPEN_CHAT_SEARCH, { source, ...defaultEventProps, ...eventProperties })
  }

  const trackClickChatSearchConversation = (
    conversation_id: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_CHAT_SEARCH_CONVERSATION, {
      conversation_id,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackClickChatSearchNewChat = (eventProperties: { [key: string]: any } = {}) => {
    track(CLICK_CHAT_SEARCH_NEW_CHAT, { ...defaultEventProps, ...eventProperties })
  }

  const trackViewRecents = (eventProperties: { [key: string]: any } = {}) => {
    track(VIEW_RECENTS, { ...defaultEventProps, ...eventProperties })
  }

  const trackClickRecentsConversation = (
    conversation_id: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_RECENTS_CONVERSATION, { conversation_id, ...defaultEventProps, ...eventProperties })
  }

  const trackToggleRecentsFavorite = (
    conversation_id: string,
    is_starred: boolean,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(TOGGLE_RECENTS_FAVORITE, {
      conversation_id,
      is_starred,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackRenameRecentsConversation = (
    conversation_id: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(RENAME_RECENTS_CONVERSATION, {
      conversation_id,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackDeleteRecentsConversation = (
    conversation_id: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(DELETE_RECENTS_CONVERSATION, {
      conversation_id,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackOpenRecentsOrganizeModal = (eventProperties: { [key: string]: any } = {}) => {
    track(OPEN_RECENTS_ORGANIZE_MODAL, { ...defaultEventProps, ...eventProperties })
  }

  const trackApplyRecentsOrganize = (
    folder_count: number,
    conversation_count: number,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(APPLY_RECENTS_ORGANIZE, {
      folder_count,
      conversation_count,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackViewArtifacts = (eventProperties: { [key: string]: any } = {}) => {
    track(VIEW_ARTIFACTS, { ...defaultEventProps, ...eventProperties })
  }

  const trackClickArtifactsTab = (
    tab_name: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_ARTIFACTS_TAB, { tab_name, ...defaultEventProps, ...eventProperties })
  }

  const trackOpenArtifact = (artifact_id: string, eventProperties: { [key: string]: any } = {}) => {
    track(OPEN_ARTIFACT, { artifact_id, ...defaultEventProps, ...eventProperties })
  }

  const trackPinArtifact = (
    artifact_id: string,
    entry: 'row' | 'viewer' | 'panel' | 'row_toggle',
    is_owned: boolean,
    via_undo = false
  ) => {
    track(PIN_ARTIFACT, { ...defaultEventProps, artifact_id, entry, is_owned, via_undo })
  }
  const trackUnpinArtifact = (
    artifact_id: string,
    entry: 'row' | 'viewer' | 'pinned_filter' | 'panel' | 'row_toggle'
  ) => {
    track(UNPIN_ARTIFACT, { ...defaultEventProps, artifact_id, entry })
  }
  const trackSearchArtifacts = (query_length: number, tab_name: string, pinned_filter: boolean) => {
    track(SEARCH_ARTIFACTS, { ...defaultEventProps, query_length, tab_name, pinned_filter })
  }
  const trackRenameArtifact = (artifact_id: string, entry: 'viewer' | 'menu') => {
    track(RENAME_ARTIFACT, { ...defaultEventProps, artifact_id, entry })
  }

  const trackClickArtifactsShare = (
    artifact_id: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_ARTIFACTS_SHARE, { artifact_id, ...defaultEventProps, ...eventProperties })
  }

  const trackOpenArtifactConversation = (artifact_id: string, entry: 'row_menu' | 'viewer') => {
    track(OPEN_ARTIFACT_CONVERSATION, { ...defaultEventProps, artifact_id, entry })
  }
  const trackToggleArtifactsPinnedFilter = (pinned_filter: boolean, tab_name: string) => {
    track(TOGGLE_ARTIFACTS_PINNED_FILTER, { ...defaultEventProps, pinned_filter, tab_name })
  }
  const trackLoadArtifactsPage = (
    page_number: number,
    tab_name: string,
    has_query: boolean,
    trigger: 'scroll' | 'retry'
  ) => {
    track(LOAD_ARTIFACTS_PAGE, { ...defaultEventProps, page_number, tab_name, has_query, trigger })
  }
  const trackViewArtifactsSearchResults = (
    query_length: number,
    result_count: number,
    has_more: boolean,
    tab_name: string,
    pinned_filter: boolean
  ) => {
    track(VIEW_ARTIFACTS_SEARCH_RESULTS, {
      ...defaultEventProps,
      query_length,
      result_count,
      has_more,
      tab_name,
      pinned_filter,
    })
  }

  const trackClickFavoriteConversation = (
    conversation_id: string,
    eventProperties: { [key: string]: any } = {}
  ) => {
    track(CLICK_FAVORITE_CONVERSATION, {
      conversation_id,
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  // Investment Profile Gate Events — onboarding gate wizard (PRD linq-alpha-server#3892)
  const trackViewInvestmentProfileGate = (eventProperties: { gate_step_count: number }) => {
    track(VIEW_INVESTMENT_PROFILE_GATE, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackCompleteInvestmentProfileStep = (eventProperties: {
    gate_step_key: InvestmentProfileStepKey
    gate_step_position: number
    gate_step_count: number
    profile_side: Side | null
  }) => {
    track(COMPLETE_INVESTMENT_PROFILE_STEP, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackSubmitInvestmentProfile = (eventProperties: {
    submit_status: 'success' | 'error'
    profile_side: Side | null
    // Shape rather than side-specific counts: which layer names apply is admin-editable data,
    // so the event stays comparable when a new side is added.
    profile_side_shape: string
    profile_category_count: number
    profile_subcategory_count: number
    profile_sector_count: number
    profile_free_text_count: number
  }) => {
    track(SUBMIT_INVESTMENT_PROFILE, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  const trackCloseInvestmentProfileGate = (eventProperties: {
    gate_step_key: InvestmentProfileStepKey
    gate_step_position: number
    gate_step_count: number
    profile_side: Side | null
    gate_exit_reason: GateExitReason
  }) => {
    track(CLOSE_INVESTMENT_PROFILE_GATE, {
      ...defaultEventProps,
      ...eventProperties,
    })
  }

  return {
    trackEventMap,
    trackOpenChatSearch,
    trackClickChatSearchConversation,
    trackClickChatSearchNewChat,
    trackViewRecents,
    trackClickRecentsConversation,
    trackToggleRecentsFavorite,
    trackRenameRecentsConversation,
    trackDeleteRecentsConversation,
    trackOpenRecentsOrganizeModal,
    trackApplyRecentsOrganize,
    trackViewArtifacts,
    trackClickArtifactsTab,
    trackOpenArtifact,
    trackPinArtifact,
    trackUnpinArtifact,
    trackSearchArtifacts,
    trackRenameArtifact,
    trackClickArtifactsShare,
    trackOpenArtifactConversation,
    trackToggleArtifactsPinnedFilter,
    trackLoadArtifactsPage,
    trackViewArtifactsSearchResults,
    trackClickFavoriteConversation,
    trackPageView,
    trackButtonClick,
    trackShow,
    trackViewMeetingAgentBlockedLink,
    trackViewProductUpdates,
    trackClickProductUpdatesLoadMore,
    trackClickRecommendedKeyword,
    trackClickRecommendedQuestion,
    trackClickTickerOption,
    //Company Page Events
    trackCompanyPageView,
    trackCompanyPageOpenPanel,
    trackClickCompanyPageTab,
    trackToggleCompanyPagePanel,
    trackClickCompanyPageSubtab,
    trackClickCompanyPageWidget,
    trackClickCompanyPageShare,
    trackClickCompanyPageExport,
    //Reddit Report Events
    trackRedditReportView,
    trackRedditReportOpenPanel,
    trackToggleRedditReportPanel,
    trackClickRedditReportWidget,
    //Feedback Events
    trackClickFeedback,

    // Briefing Agent Events
    trackViewBriefingAgentPage,
    trackOpenBriefingAgentModal,
    trackCloseBriefingAgentModal,
    trackClickBriefingAgentCard,
    trackSelectBriefingAgentSource,
    trackChangeBriefingAgentFrequency,
    trackAddBriefingAgentTicker,
    trackRemoveBriefingAgentTicker,
    trackCompleteBriefingAgentSetup,
    trackToggleBriefingAgentStatus,
    trackDeleteBriefingAgent,
    trackSearchBriefingAgentHistory,
    trackFilterBriefingAgentHistory,
    trackClickBriefingAgentHistoryPage,
    trackToggleBriefingAgentHistoryPanel,
    trackClickBriefingAgentHistoryCard,
    trackClickBriefingAgentHistoryButton,
    trackSelectBriefingAgentSkill,
    trackClickBriefingAgentPreview,

    //Primer Events
    trackClickPrimerExport,

    //Analytics Trending Events
    trackClickAnalyticsTrendingQuestion,

    //Analytics Skill Events
    trackClickAnalyticsSkill,

    // Storage Vault Folder Events
    trackClickStorageCreateFolder,
    trackClickStorageMoveFile,

    // RMS Viewer Events
    trackClickRmsViewerDownload,

    // Investment Profile Gate Events
    trackViewInvestmentProfileGate,
    trackCompleteInvestmentProfileStep,
    trackSubmitInvestmentProfile,
    trackCloseInvestmentProfileGate,
  }
}

export default useAmplitudeTracking
